import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    ArrowLeft, Calendar, User, Search, IndianRupee, 
    Sun, Moon, Filter, ChevronDown, Layers, X, Clock,
    HelpCircle, PlayCircle
} from 'lucide-react';
import { useSmartGuide } from './useSmartGuide';

import Card from '../../ui/Card';
import Tabs from '../../ui/Tabs';

const API_BASE = 'https://mukadampro.shirpur.store'; 

// --- HELPER UTILS ---
const getLoomNumber = (id) => {
    if (!id) return 0;
    const str = String(id);
    const digits = str.replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : 0;
};

const getHindiDayName = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const days = [" इतवार", " पीर", " मंगल", " बुध", " जुमेरात", " जुम्मा", " सनीचर"];
    return days[d.getDay()];
};

// Calculate duration for display
const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    try {
        const parseToMins = (timeStr) => {
            let isPM = timeStr.toUpperCase().includes('PM');
            let isAM = timeStr.toUpperCase().includes('AM');
            let cleanTime = timeStr.replace(/AM|PM/gi, '').trim();
            let [h, m] = cleanTime.split(':');
            let hours = parseInt(h, 10);
            let mins = parseInt(m, 10) || 0;
            
            if (isPM && hours < 12) hours += 12;
            if (isAM && hours === 12) hours = 0;
            
            return (hours * 60) + mins;
        };

        let startMins = parseToMins(start);
        let endMins = parseToMins(end);

        if (endMins < startMins) {
            endMins += 24 * 60; // Crosses midnight (Next day)
        }
        
       const totalDiffMins = endMins - startMins;
        const diffHours = Math.floor(totalDiffMins / 60);
        const diffMins = totalDiffMins % 60;
        
        const paddedMins = diffMins.toString().padStart(2, '0');
        
        // Naya Format: 14H : 00M DURATION
        return `${diffHours}H : ${paddedMins}M DURATION`;
    } catch (e) {
        return null;
    }
};

const formatDisplayTime = (t) => {
    if (!t) return '';
    try {
        if(t.includes('AM') || t.includes('PM')) return t;
        let [h, m] = t.split(':');
        let hr = parseInt(h, 10);
        const ampm = hr >= 12 ? 'PM' : 'AM';
        hr = hr % 12 || 12;
        return `${hr}:${m} ${ampm}`;
    } catch (e) {
        return t;
    }
};

const getHindiTimeMsg = (timeStr) => {
    if(!timeStr) return "";
    const isPM = timeStr.toUpperCase().includes('PM');
    const clean = timeStr.replace(/AM|PM/gi, '').trim();
    let [h, m] = clean.split(':');
    h = parseInt(h, 10);
    m = parseInt(m, 10) || 0;

    let period = "सुबह";
    if (isPM) {
        if (h === 12 || h < 4) period = "दोपहर";
        else if (h >= 4 && h < 8) period = "शाम";
        else period = "रात";
    } else {
        if (h === 12 || h < 4) period = "रात";
    }

    if (m > 0) {
        return `${period} ${h} बजकर ${m} मिनट`;
    } else {
        return `${period} ${h} बजे`;
    }
};

const getDutyTimeVoiceMsg = (start, end, recordDate) => {
    if (!start || !end || !recordDate) return "";
    const durStr = calculateDuration(start, end); 
    if (!durStr) return "";

    let [hrs, mins] = durStr.split(' ');
    hrs = parseInt(hrs.replace('H', ''));
    mins = parseInt(mins.replace('M', ''));
    
    let durMsg = `${hrs} घंटे${mins > 0 ? ' ' + mins + ' मिनट' : ''}`;

    const startMsg = getHindiTimeMsg(start);
    const endMsg = getHindiTimeMsg(end);

    const parseToMins = (t) => {
        let isPM = t.toUpperCase().includes('PM');
        let [h, m] = t.replace(/AM|PM/gi, '').trim().split(':');
        h = parseInt(h, 10);
        if (isPM && h < 12) h += 12;
        if (!isPM && h === 12) h = 0;
        return (h * 60) + (parseInt(m, 10) || 0);
    };

    const isNextDay = parseToMins(end) < parseToMins(start);

    const d = new Date(recordDate);
    const days = [' इतवार', ' पीर', ' मंगल', ' बुध', ' जुमेरात', ' जुम्मा', ' सनीचर'];
    const startDayName = days[d.getDay()]; 
    
    let endDayName = startDayName;
    if (isNextDay) {
        const nextD = new Date(d);
        nextD.setDate(d.getDate() + 1); 
        endDayName = days[nextD.getDay()];
    }

    return `${startDayName} की ${startMsg} से लेकर ${endDayName} की ${endMsg} तक, टोटल ${durMsg} ड्यूटी की है। `;
};

// --- STAT CARD COMPONENT ---
const StatCard = ({ label, value, valueColor, icon: Icon, color, onClick }) => (
  <div onClick={onClick} style={{
    background: 'rgba(255, 255, 255, 0.25)', 
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRadius: '16px', padding: '12px',
    boxShadow: '0 6px 16px rgba(0,0,0,0.05)', 
    border: '1px solid rgba(255, 255, 255, 0.3)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    minHeight: '64px', flex: 1, cursor: onClick ? 'pointer' : 'default'
  }}>
    <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
        <h3 style={{fontSize: '8px', fontWeight: '600', color: '#444', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px'}}>{label}</h3>
        <div style={{fontSize: '12px', fontWeight: '700', color: valueColor || '#111', letterSpacing: '-0.2px'}}>{value}</div>
    </div>
    <div style={{width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 2px 6px rgba(255,255,255,0.5)', flexShrink: 0, marginLeft: '10px'}}>
        <Icon size={16} color={color || '#111'} strokeWidth={2.5}/>
    </div>
  </div>
);

const BadliHistory = ({ onBack, appBg, globalGuide }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedAbsentWorker, setSelectedAbsentWorker] = useState(''); 
    const [selectedBadliWorker, setSelectedBadliWorker] = useState('');   
    const [dateFilter, setDateFilter] = useState('');
    const [activeShift, setActiveShift] = useState('All'); 

    const { 
        voiceAssist, handleVoiceClick, speakHinglish, handleFocusVoice,
        showGuideModal, setShowGuideModal, activeGuideFlow, currentGuideMessage,
        startSmartGuide, endSmartGuide, advanceNextStep
    } = globalGuide;
    
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const searchRef = useRef(null);
    const prevFilteredLength = useRef(null);

    useEffect(() => {
        fetchHistory();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };
        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded]);

    const fetchHistory = async () => {
        try {
            const res = await fetch(`${API_BASE}/get_badli_history.php`);
            const json = await res.json();
            if (json.status === 'success') {
                setData(json.data);
                setFilteredData(json.data);
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const absentWorkersList = useMemo(() => {
        let temp = data;
        if (activeShift !== 'All') {
            temp = temp.filter(item => item.shift && item.shift.toLowerCase() === activeShift.toLowerCase());
        }
        const names = temp.map(item => item.original_worker_name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [data, activeShift]);

    const badliWorkersList = useMemo(() => {
        let temp = data;
        if (activeShift !== 'All') {
            temp = temp.filter(item => item.shift && item.shift.toLowerCase() === activeShift.toLowerCase());
        }
        const names = temp.map(item => item.badli_worker_name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [data, activeShift]);

    useEffect(() => {
        let temp = data;
        if (activeShift !== 'All') {
            temp = temp.filter(item => item.shift && item.shift.toLowerCase() === activeShift.toLowerCase());
        }
        if (selectedAbsentWorker) {
            temp = temp.filter(item => item.original_worker_name === selectedAbsentWorker);
        }
        if (selectedBadliWorker) {
            temp = temp.filter(item => item.badli_worker_name === selectedBadliWorker);
        }
        if (dateFilter) {
            temp = temp.filter(item => item.date === dateFilter);
        }
        setFilteredData(temp);
    }, [selectedAbsentWorker, selectedBadliWorker, dateFilter, activeShift, data]);

    const isFilterActive = activeShift !== 'All' || selectedAbsentWorker || selectedBadliWorker || dateFilter;

    useEffect(() => {
        if (!loading && isFilterActive) {
            if (filteredData.length === 0 && prevFilteredLength.current !== 0) {
                if (voiceAssist) {
                    speakHinglish("कोई रिज़ल्ट नहीं मिला।");
                }
            }
            prevFilteredLength.current = filteredData.length;
        } else {
            prevFilteredLength.current = null;
        }
    }, [filteredData.length, loading, isFilterActive, voiceAssist, speakHinglish]);

    const handleClearAndClose = (e) => {
        e.stopPropagation(); 
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        setSelectedAbsentWorker('');
        setSelectedBadliWorker('');
        setDateFilter('');
        setActiveShift('All');
        setIsExpanded(false);
    };

    const groupedList = useMemo(() => {
        const groups = {};
        
        filteredData.forEach(item => {
            const key = `${item.date}_${item.original_worker_name}_${item.badli_worker_name}_${item.shift}_${item.start_time}_${item.end_time}`;
            
            if (!groups[key]) {
                groups[key] = {
                    date: item.date,
                    shift: item.shift,
                    original_worker_name: item.original_worker_name,
                    badli_worker_name: item.badli_worker_name,
                    start_time: item.start_time,
                    end_time: item.end_time,
                    looms: [],
                    total_meters: 0,
                    total_earning: 0
                };
            }
            
            const m = parseFloat(item.production_meters || 0);
            const rate = parseFloat(item.rate_per_meter || 0);
            const earn = m * rate; 
            
            groups[key].looms.push({
                id: item.loom_id,
                meters: m
            });
            
            groups[key].total_meters += m;
            groups[key].total_earning += earn;
        });

        return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [filteredData]);

   const totalProduction = filteredData.reduce((acc, curr) => acc + parseFloat(curr.production_meters || 0), 0);
    const totalEntries = groupedList.length;

    // 🌟 100% LAG-FREE PAGINATION STATE 🌟
    const [visibleCount, setVisibleCount] = useState(15);
    
    // जब भी कोई फ़िल्टर चेंज हो, तो लिस्ट को वापस 15 पर सेट करें
    useEffect(() => {
        setVisibleCount(15);
    }, [groupedList]);

    // जैसे ही वर्कर नीचे स्क्रॉल करेगा, 15 नए कार्ड स्मूथली ऐड हो जाएंगे
    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 500) {
            if (visibleCount < groupedList.length) {
                setVisibleCount(prev => prev + 15);
            }
        }
    };

    return (
        <div style={styles.container} onScroll={handleScroll}>
           
            {/* 🌟 NAYA: 14px Font Force Lock for all child components 🌟 */}
            <style>{`
                * { font-size: 14px !important; }
            `}</style>
            
            {/* ===== 🌟 PREMIUM STICKY HEADER WRAPPER 🌟 ===== */}
            <div style={{ position: 'sticky', top: 0, zIndex: 1000, pointerEvents: activeGuideFlow ? 'none' : 'auto' }}>
                <div style={{...styles.header, background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)'}}>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex:1}}>
                           <button 
                                onClick={(e) => {
                                    if (window.speechSynthesis) window.speechSynthesis.cancel();
                                    onBack(e);
                                }} 
                                style={{...styles.backBtn, background: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'}}
                            >
                                <ArrowLeft size={18} color="#475569"/>
                            </button>
                            <div>
                                <h1 style={{fontSize:'16px', fontWeight:'700', color:'#0F172A', margin:0, letterSpacing: '-0.5px'}}>Badli Register</h1>
                                <span style={{fontSize:'8px', color:'#64748B', fontWeight:'600', display:'block', marginTop:'2px', letterSpacing:'0.5px', textTransform: 'uppercase'}}>Manage substitute duties</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{...styles.content, pointerEvents: activeGuideFlow ? 'none' : 'auto'}}>
                
              {/* ==========================================
                    PREMIUM STATS CARDS (Entries & Prod)
                ========================================== */}
                {/* 🌟 FIX: h3 टैग को सीधा टारगेट किया गया ताकि साइज़ 12px 100% अप्लाई हो जाए और Text Uppercase रहे 🌟 */}
                <style>{`.badli-stats-box h3 { font-size: 12px !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; }`}</style>
                
                <div className="badli-stats-box" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    
                    <Card 
                        onClick={(e) => handleVoiceClick && handleVoiceClick(e, ` इस लिस्ट में टोटल ${totalEntries} बदली एंट्रीज हैं।`, null)}
                        title="TOTAL BADLI DUTY ENTRIES"
                        icon={Filter}
                        iconBg="linear-gradient(135deg, #F3E8FF, #D8B4FE)"
                        iconColor="#7E22CE"
                        style={{ textAlign: 'center' }}
                    >
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', letterSpacing: '-0.2px' }}>
                            {totalEntries}
                        </div>
                    </Card>

                    <Card 
                        onClick={(e) => handleVoiceClick && handleVoiceClick(e, ` इन सभी बदली रिकॉर्ड्स का टोटल प्रोडक्शन ${totalProduction.toFixed(0)} मीटर हुआ है।`, null)}
                        title="TOTAL PRODUCTION"
                        icon={Layers}
                        iconBg="linear-gradient(135deg, #E0F2FE, #7DD3FC)"
                        iconColor="#0284C7"
                        style={{ textAlign: 'center' }}
                    >
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', letterSpacing: '-0.2px' }}>
                            {totalProduction.toFixed(0)} <span style={{fontSize: '10px', fontWeight: '500', color: '#475569'}}>Meter</span>
                        </div>
                    </Card>
                </div>

               {/* SEARCH SECTION */}
                <div ref={searchRef} style={{marginBottom:'16px'}}>
                    <div style={{ 
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)', 
                        backdropFilter: 'blur(24px)', 
                        borderRadius: '20px', 
                        padding: '12px', 
                        boxShadow: '0 10px 25px rgba(0,0,0,0.04), inset 0 2px 0 rgba(255,255,255,1)', 
                        border: '1px solid rgba(255,255,255,0.7)' 
                    }}>
                        {!isExpanded ? (
                            <div id="open-search" onClick={(e) => handleVoiceClick(e, " यह Search Filter है, इसकी मदद से आप बदली Production सर्च कर सकते हैं।", () => setIsExpanded(true), 'open-search')} 
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.04)', transition: 'all 0.2s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Search size={14} color={isFilterActive ? '#0F172A' : '#64748B'}/>
                                    <span style={{ color: isFilterActive ? '#0F172A' : '#64748B', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '10px' }}>
                                        {isFilterActive ? 'FILTERS ACTIVE (TAP TO EDIT)' : 'SEARCH FILTERS...'}
                                    </span>
                                </div>
                                <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                    {isFilterActive && (
                                        <span style={{background:'#E2E8F0', color:'#0F172A', fontSize:'8px', padding:'3px 8px', borderRadius:'8px', fontWeight: '600'}}>ACTIVE</span>
                                    )}
                                    <div style={{display:'flex', color: '#94A3B8'}}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ background: 'transparent', animation: 'slideDown 0.2s ease-out' }}>
                                {/* 🌟 WorkerHistory Style: कस्टम क्लासेज़ फॉर लाइट प्रीमियम लुक */}
                                <style>{`
                                    .badli-search-box-text { font-size: 10px !important; }
                                    .badli-input-text { font-size: 11px !important; }
                                    .badli-select-bg { 
                                        background-color: #F8FAFC !important; 
                                        border: 1px solid #E2E8F0 !important;
                                        color: #0F172A !important;
                                    }
                                `}</style>
                                
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                        <Search size={14} color="#0F172A"/>
                                        <span className="badli-search-box-text" style={{fontWeight:'700', color:'#0F172A', textTransform:'uppercase', letterSpacing:'0.5px'}}>SEARCH FILTERS</span>
                                    </div>
                                    <button 
                                        onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " सर्च फिल्टर हटाने के लिए इस पर दोबारा क्लिक करें।", handleClearAndClose, 'btn-clear') : handleClearAndClose(e); }} 
                                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', color: '#DC2626', transition: 'background 0.2s' }}
                                    >
                                        <X size={11} /> <span className="badli-search-box-text" style={{ fontWeight: '500', marginLeft: '3px' }}>CLEAR</span>
                                    </button>
                                </div>

                                <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                   {/* 🌟 PREMIUM CUSTOM TABS FOR SHIFT SELECTION 🌟 */}
                                    <div style={{ 
                                        display: 'flex', 
                                        background: '#F1F5F9', /* 🌟 अनसिलेक्टेड बॉक्स का डार्क/ग्रे बैकग्राउंड */
                                        padding: '4px', 
                                        borderRadius: '16px', /* पिल (Pill) शेप */
                                        marginBottom: '12px',
                                        boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.05)', /* अंदर की तरफ शैडो ताकि 3D कटआउट लगे */
                                        border: '1px solid rgba(226, 232, 240, 0.8)'
                                    }}>
                                        <button 
                                            onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " आपने ऑल शिफ्ट चुना है, इसमें आप Day और Night दोनों शिफ्ट की बदली Production History देख सकते हैं।", () => setActiveShift('All'), 'tab-all') : setActiveShift('All'); }} 
                                            style={activeShift === 'All' 
                                                ? { flex: 1, padding: '8px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '700', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center' } 
                                                : { flex: 1, padding: '8px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                        >
                                            ALL
                                        </button>

                                        <button 
                                            onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " आपने Day शिफ्ट चुना है, इसमें आप सिर्फ Day शिफ्ट की बदली Production History देख सकते हैं।", () => setActiveShift('Day'), 'tab-day') : setActiveShift('Day'); }} 
                                            style={activeShift === 'Day' 
                                                ? { flex: 1, padding: '8px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '700', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' } 
                                                : { flex: 1, padding: '8px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}
                                        >
                                            <Sun size={12} color={activeShift === 'Day' ? '#0F172A' : '#64748B'}/> DAY
                                        </button>

                                        <button 
                                            onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " आपने Night शिफ्ट चुना है, इसमें आप सिर्फ Night शिफ्ट की बदली Production History देख सकते हैं।", () => setActiveShift('Night'), 'tab-night') : setActiveShift('Night'); }} 
                                            style={activeShift === 'Night' 
                                                ? { flex: 1, padding: '8px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '700', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' } 
                                                : { flex: 1, padding: '8px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}
                                        >
                                            <Moon size={12} color={activeShift === 'Night' ? '#0F172A' : '#64748B'}/> NIGHT
                                        </button>
                                    </div>

                                    <div style={{position:'relative'}}>
                                        <select 
                                            id="select-absent-worker" 
                                            className="badli-input-text badli-select-bg"
                                            style={{...styles.input, opacity: '0.8', textTransform: 'uppercase', appearance: 'none', paddingRight: '30px'}} 
                                            value={selectedAbsentWorker} 
                                            onFocus={() => handleFocusVoice(" Shift Chunne के बाद Filter Absent Worker पर क्लिक करें, जिस Worker ने बदली दी थी उस वर्कर को चुनें।")}
                                            onChange={(e) => setSelectedAbsentWorker(e.target.value)}
                                        >
                                            <option value="">FILTER ABSENT WORKER</option>
                                            {absentWorkersList.map((name, idx) => (
                                                <option key={idx} value={name}>{name}</option>
                                            ))}
                                        </select>
                                        <div style={{position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none'}}><ChevronDown size={14} color="#64748B"/></div>
                                    </div>

                                    <div style={{display:'flex', gap:'8px'}}>
                                        {/* 🌟 Badli Worker Box (अब यह थोड़ा छोटा हो जाएगा) */}
                                        <div style={{flex: 1, position:'relative'}}>
                                            <select 
                                                id="select-badli-worker" 
                                                className="badli-input-text badli-select-bg"
                                                style={{...styles.input, opacity: '0.8', textTransform: 'uppercase', appearance: 'none', paddingRight: '30px'}} 
                                                value={selectedBadliWorker} 
                                                onFocus={() => handleFocusVoice(" बदली ड्यूटी करने वाले वर्कर का नाम चुनें (अगर नहीं पता, तो खाली छोड़ देंं")}
                                                onChange={(e) => setSelectedBadliWorker(e.target.value)}
                                            >
                                                <option value="">FILTER BADLI WORKER</option>
                                                {badliWorkersList.map((name, idx) => (
                                                    <option key={idx} value={name}>{name}</option>
                                                ))}
                                            </select>
                                            <div style={{position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none'}}><ChevronDown size={14} color="#64748B"/></div>
                                        </div>
                                        
                                        {/* 🌟 Date Box (चौड़ाई 140px कर दी गई है) */}
                                        <div style={{width:'140px', position: 'relative'}}>
                                            <input 
                                                id="input-filter-date"
                                                type="date"
                                                className="badli-input-text badli-select-bg"
                                                style={{...styles.input, opacity: '0.8', textTransform: 'uppercase', color: '#0F172A'}}
                                                value={dateFilter}
                                                onFocus={() => handleFocusVoice(" जिस दिन की बदली चेक करनी है वो डेट सेलेक्ट करें।")}
                                                onChange={(e)=>setDateFilter(e.target.value)}
                                                onClick={(e) => { try { if(e.target.showPicker) e.target.showPicker(); } catch(err) { console.log(err); } }}
                                            />
                                            {/* 🌟 सॉलिड मास्क जो ब्राउज़र के 'dd-mm-yyyy' को छिपा देगा लेकिन कैलेंडर आइकॉन को दिखने देगा */}
                                            {!dateFilter && (
                                                <div style={{
                                                    position: 'absolute', left: '2px', top: '2px', bottom: '2px', right: '35px', 
                                                    background: '#F8FAFC', 
                                                    display: 'flex', alignItems: 'center', paddingLeft: '10px', 
                                                    pointerEvents: 'none', color: '#5f5f5fff', fontSize: '10px', fontWeight: '500', 
                                                    borderRadius: '10px'
                                                }}>
                                                    DATE
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

               {/* LIST */}
                {loading ? (
                    <div style={{textAlign:'center', padding:'32px', color:'#111', fontWeight:'500', fontSize:'8px'}}>Loading Data...</div>
                ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                        {groupedList.slice(0, visibleCount).map((group, index) => {
                            const uniqueCardKey = `${group.date}_${group.original_worker_name}_${group.start_time}`;
                            const hasProduction = group.total_meters > 0;
                            const displayLooms = group.looms.sort((a,b) => getLoomNumber(a.id) - getLoomNumber(b.id));
                            const isNightShift = group.shift.toLowerCase() === 'night';
                            
                            const absName = group.original_worker_name || 'absent worker';
                            const badliName = group.badli_worker_name;
                            const shiftNameHindi = group.shift.toLowerCase() === 'day' ? 'दिन' : 'रात';
                            
                            let timeVoice = "";
                            if (group.start_time && group.end_time && group.date) {
                                timeVoice = getDutyTimeVoiceMsg(group.start_time, group.end_time, group.date);
                            }

                            const dObj = new Date(group.date);
                            const monthList = [' जनवरी', ' फरवरी', ' मार्च', ' अप्रैल', ' मई', ' जून', ' जुलाई', ' अगस्त', ' सितम्बर', ' अक्टूबर', ' नवंबर', ' दिसंबर'];
                           const cleanDate = `${dObj.getDate()} ${monthList[dObj.getMonth()]}`; 
                            
                            // 🌟 NAYA: Historical Rate Calculation 🌟
                            const dayRate = group.total_meters > 0 ? (group.total_earning / group.total_meters) : 0;

                            const voiceDetailMsg = `${cleanDate} को, ${timeVoice} (${shiftNameHindi} शिफ्ट में ${absName} की जगह ${badliName} ने बदली ड्यूटी की थी।) इस ड्यूटी का टोटल प्रोडक्शन ${group.total_meters.toFixed(1)} मीटर हुआ है, जिसके लिए ${badliName} को ${group.total_earning.toFixed(0)} रुपये की सैलरी देनी होगी।`;                            
                            return (
                                <div 
                                    key={uniqueCardKey}
                                    style={{...styles.card, cursor: voiceAssist ? 'pointer' : 'default'}}
                                    onClick={(e) => handleVoiceClick(e, voiceDetailMsg, null)}
                                >
                                    <div style={{
                                        padding: '10px 12px', 
                                        background: 'rgba(255,255,255,0.2)',
                                        borderTop: '4px solid rgba(234, 179, 8, 0.6)', /* 🌟 बदली आइकॉन वाली टॉप लाइन (Amber) 🌟 */
                                        display: 'flex', flexDirection: 'column', gap: '6px'
                                    }}>
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                           <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                                                <Calendar size={12} color="#111" />
                                                <div style={{fontSize:'11px', fontWeight:'600', color: '#111'}}>
                                                    {new Date(group.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </div>
                                                {/* 🌟 FIX: Shift Badge padding and letterSpacing updated */}
                                                <div style={{
                                                    fontSize:'8px', 
                                                    background: isNightShift ? '#111' : 'rgba(255,255,255,0.6)',
                                                    color: isNightShift ? '#ffffff' : '#111', 
                                                    border: isNightShift ? '1px solid #111' : '1px solid rgba(255,255,255,0.4)',
                                                    padding:'2px 5px', borderRadius:'6px', fontWeight:'600', textTransform: 'uppercase', letterSpacing: '0.3px',
                                                    display: 'inline-block'
                                                }}>
                                                    {group.shift}
                                                </div>
                                            </div>
                                           
                                        </div>

                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'stretch', marginTop:'8px', gap:'8px'}}>
                                            <div style={{flex: '1', width: '50%', background: '#ffffff', padding: '6px 8px', borderRadius: '8px', display: 'flex', flexDirection: 'column', border: '1px solid #ffffff'}}>
                                                <div style={{fontSize:'8px', color:'rgba(0, 0, 0, 0.9)', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.3px'}}>Absent Worker</div>
                                                {/* 🌟 FIX: Worker name size increased to 14px with premium bold look */}
                                                <div style={{fontSize:'14px', color:'rgba(0, 0, 0, 0.4)', fontWeight:'700', letterSpacing:'-0.3px', textTransform:'capitalize', whiteSpace:'normal', wordBreak:'break-word'}}>{group.original_worker_name || 'N/A'}</div>
                                            </div>

                                            <div style={{flex: '1', width: '50%', background: '#ffffff', padding: '6px 8px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', border: '1px solid #ffffff'}}>
                                                <div style={{fontSize:'8px', color:'rgba(0, 0, 0, 0.9)', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.3px'}}>Badli By</div>
                                                {/* 🌟 FIX: Worker name size increased to 14px with premium bold look */}
                                                <div style={{fontSize:'14px', color:'#0F172A', fontWeight:'700', letterSpacing:'-0.3px', textTransform:'capitalize', whiteSpace:'normal', wordBreak:'break-word', textAlign: 'right'}}>{group.badli_worker_name}</div>
                                            </div>
                                        </div>
                                        
                                     {/* 🌟 NEW TIME AND DURATION DESIGN 🌟 */}
                                        {(group.start_time || group.end_time) && (
                                            <div style={{display:'flex', alignItems:'center', gap:'3px', marginTop:'4px', background:'#ffffff', padding:'2px 5px', borderRadius:'6px', border:'1px solid #e2e8f0', width:'fit-content', boxShadow:'0 2px 6px rgba(0,0,0,0.02)'}}>
                                                <Clock size={10} color="#2563EB" />
                                                <span style={{fontSize:'8px', color:'#0F172A', fontWeight:'600'}}>
                                                    {formatDisplayTime(group.start_time)} - {formatDisplayTime(group.end_time)} 
                                                </span>

                                                <style>{`.locked-badli-duration { color: #64748B !important; }`}</style>

                                                {calculateDuration(group.start_time, group.end_time) && (
                                                    <span className="locked-badli-duration" style={{fontSize:'8px', fontWeight:'600', letterSpacing:'0.3px', textTransform:'uppercase'}}>
                                                        <span className="locked-badli-duration" style={{margin:'0 4px', letterSpacing:'0'}}>•</span> 
                                                        {calculateDuration(group.start_time, group.end_time)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    

                                  {/* 🌟 यहाँ बीच की लाइन को भी ग्रीन से बदली कलर (Amber) में कर दिया गया है 🌟 */}
                                  <div style={{ width: '100%', margin: '8px 0', lineHeight: 0 }}>
                                      <svg width="100%" height="1" preserveAspectRatio="none" style={{ display: 'block' }}>
                                          <defs>
                                              <linearGradient id={`fadeBadli-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                                  <stop offset="0%" stopColor="rgba(234,179,8,0)" />
                                                  <stop offset="50%" stopColor="rgba(234,179,8,0.8)" />
                                                  <stop offset="100%" stopColor="rgba(234,179,8,0)" />
                                              </linearGradient>
                                          </defs>
                                          <rect x="0" y="0" width="100%" height="1" fill={`url(#fadeBadli-${index})`} shapeRendering="crispEdges" />
                                      </svg>
                                  </div>

                                    <div style={{padding:'10px'}}>
                                        {!hasProduction ? (
                                            <div style={{
                                                background: 'rgba(255,255,255,0.3)', borderRadius:'12px', padding:'10px', 
                                                border:'1px dashed rgba(255,255,255,0.5)', textAlign: 'center'
                                            }}>
                                                <span style={{fontSize:'8px', fontWeight:'600', color:'#444', textTransform:'uppercase', letterSpacing:'0.5px'}}>
                                                    No Production
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ padding: '8px 4px' }}>
                                                    <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                                                        {displayLooms.map((loom, i) => (
                                                            <div key={i} style={{
                                                                flex: '1 0 30%', minWidth: '70px', 
                                                                background: '#ffffff', 
                                                                border: loom.meters > 0 ? '1px solid #e2e8f0' : '1px dashed #cbd5e1', 
                                                                boxShadow: '0 4px 10px rgba(0,0,0,0.06)', 
                                                                borderRadius: '10px', padding: '10px 6px', textAlign: 'center',
                                                                opacity: loom.meters > 0 ? 1 : 0.7
                                                            }}>
                                                                <div style={{fontSize:'8px', color:'#64748B', fontWeight:'600', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.5px'}}>
                                                                    Loom {String(loom.id).padStart(2, '0')}
                                                                </div>
                                                                <div style={{fontSize:'12px', fontWeight:'700', color:'#0F172A'}}>
                                                                  {loom.meters.toFixed(0)} <span style={{fontSize:'8px', fontWeight:'600', color:'#2563EB'}}>Meter</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            {/* 🌟 NAYA LOGIC: Box ke bottom me Total Meter (Left) aur Earning + Rate (Right) 🌟 */}
                                                <div style={{marginTop:'12px', display:'flex', justifyContent:'space-between', alignItems:'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0'}}>
                                                    
                                                    {/* LEFT SIDE: Total Production */}
                                                    <div style={{display:'flex', flexDirection:'column'}}>
                                                        {/* 🌟 FIX: letterSpacing 0.3px added */}
                                                        <span style={{fontSize:'8px', color:'#64748B', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.3px'}}>Total Production</span>
                                                        <span style={{fontSize:'14px', fontWeight:'700', color: '#0F172A'}}>
                                                            {group.total_meters.toFixed(1)} <span style={{fontSize:'8px', fontWeight:'600', color:'#2563eb', marginLeft:'2px'}}>Meter</span>
                                                        </span>
                                                    </div>
                                                    
                                                    {/* RIGHT SIDE: Historical Rate & Earning */}
                                                    <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                                                        <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom: '2px'}}>
                                                            {/* 🌟 FIX: letterSpacing 0.3px added */}
                                                            <span style={{fontSize:'8px', color:'#64748B', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.3px'}}>Total Earning</span>
                                                            
                                                            {/* 🌟 FIX: Rate badge ko 8px aur split style me update kiya */}
                                                            <div style={{background: '#e2e8f0', padding: '2px 5px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '3px'}}>
                                                                <span style={{fontSize:'8px', color:'#64748B', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.3px'}}>RATE:</span>
                                                                <span style={{fontSize:'8px', fontWeight:'700', color: '#0F172A'}}>₹{dayRate.toFixed(2)}/M</span>
                                                            </div>
                                                        </div>
                                                        <span style={{fontSize:'14px', color:'#16a34a', fontWeight:'700', display:'flex', alignItems:'center', gap:'2px'}}>
                                                            <IndianRupee size={12}/> {(group.total_earning || 0).toFixed(0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {groupedList.length === 0 && (
                             <div style={{textAlign:'center', padding:'32px', color:'#111', fontSize:'11px', fontWeight:'500'}}>कोई रिज़ल्ट नहीं मिला।</div>
                        )}
                        
                        {/* 🌟 LAZY LOADING INDICATOR 🌟 */}
                        {visibleCount < groupedList.length && (
                            <div style={{textAlign: 'center', padding: '16px', fontSize: '11px', color: '#64748B', fontWeight: '600'}}>
                                Loading more records...
                            </div>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
};

// --- STYLES OBJECT ---
const styles = {
    container: { 
        fontSize: '14px', zoom: 0.95,
        minHeight: '100vh', background: 'transparent', paddingBottom: '85px', 
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
        overflowY: 'auto', zIndex: 100,
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain'
        /* GPU क्रैश रोकने के लिए translateZ(0) को यहाँ से हटा दिया गया है */
    },
    header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: 'none', display: 'flex', alignItems: 'center', justifyContent:'space-between', willChange: 'transform, backdrop-filter' },
    header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: 'none', display: 'flex', alignItems: 'center', justifyContent:'space-between' }, 
    backBtn: { background: 'rgba(255,255,255,0.4)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', padding: '4px', display:'flex', alignItems:'center' },
    content: { padding: '16px', maxWidth: '600px', margin: '0 auto' },
    
    // Search Strip (Collapsed)
    searchStrip: {
        background: 'rgba(255,255,255,0.3)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '16px',
        padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer', fontSize: '8px', fontWeight: '600',
        transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    },

    // Expanded Box
    expandedBox: {
        background: 'rgba(255, 255, 255, 0.35)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.4)',
        padding: '12px', boxShadow: '0 10px 24px -5px rgba(0,0,0,0.1)',
        animation: 'zoomIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
    },

    // Inputs
    input: { width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.5)', fontSize: '8px', outline: 'none', background: 'rgba(255,255,255,0.6)', color:'#111', fontWeight:'600', boxSizing: 'border-box', transition: 'all 0.2s', appearance:'none' },
    
    dropdownIcon: { position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' },
    dateOverlay: { position: 'absolute', left: '12px', top: '0', bottom: '0', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#555', fontSize: '8px', fontWeight: '600' },
    
    card: { 
        background: '#ffffff', 
        borderRadius: '20px', 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)', 
        border: '1px solid rgba(220, 220, 220, 0.5)', 
        overflow: 'hidden',
        contentVisibility: 'auto',
        containIntrinsicSize: '150px',
        contain: 'paint layout',
        willChange: 'transform, opacity',
        transform: 'translateZ(0)'
    },

    };

export default BadliHistory;